import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import "../styles/shared-ui.css";
import { useAppContext } from "../context/AppContext";
import { getFloorWithRelations } from "../services/floorService";
import FloorLayoutDesigner from "../components/ui/FloorLayoutDesigner";
import FloorDeskLayout from "../components/ui/FloorDeskLayout";
import { useFloorForm } from "../hooks/useFloorForm";

/**
 * One Floor page in two modes.
 *
 * `edit` is the working page: floor details, the design layout (what each grid
 * cell is), and the desk layout. `view` is the same page read-only — it shows
 * the design rather than offering to change it, which is what the /floors/:id
 * route is for.
 *
 * Both layouts own their own data. This page holds only the floor's details and
 * a version counter that tells the desk grid to re-read the plan after the
 * designer has changed it.
 */
const FloorEditPage = ({ mode = "edit" }) => {
  const readOnly = mode === "view";
  const { floorId: contextFloorId } = useAppContext();
  const { floorId: routeParamFloorId } = useParams();

  const effectiveFloorId = useMemo(() => {
    const fromRoute = Number(routeParamFloorId);
    if (Number.isFinite(fromRoute) && fromRoute > 0) return fromRoute;
    if (contextFloorId != null) return contextFloorId;
    try {
      const v = Number(localStorage.getItem("floorEditId"));
      if (Number.isFinite(v) && v > 0) return v;
    } catch (_) {}
    return null;
  }, [routeParamFloorId, contextFloorId]);

  const { form, setForm, saving, save, backToList } = useFloorForm({
    mode: "edit",
    floorId: effectiveFloorId,
  });

  const [buildings, setBuildings] = useState([]);
  const [loading, setLoading] = useState(true);

  // Bumped when the designer persists a change, so the desk grid re-reads the
  // rooms. Deliberately not a full page reload: the analyst may have edited
  // Floor Code or Name without saving, and reloading would discard it.
  const [layoutVersion, setLayoutVersion] = useState(0);
  const handleLayoutChange = useCallback(
    () => setLayoutVersion((n) => n + 1),
    [],
  );

  // And the other way: the designer reads each room's tables for its seat cap
  // and census, so adding or removing a desk has to reach it too.
  const [desksVersion, setDesksVersion] = useState(0);
  const handleDesksChange = useCallback(
    () => setDesksVersion((n) => n + 1),
    [],
  );

  const buildingMap = useMemo(
    () =>
      Object.fromEntries(
        (buildings || []).map((b) => [Number(b.building_id), b]),
      ),
    [buildings],
  );

  const selectedBuildingIdValue =
    form.building_id === null || form.building_id === undefined
      ? ""
      : String(form.building_id);

  const hasSelectedBuildingOption = useMemo(() => {
    if (!selectedBuildingIdValue) return false;
    return (buildings || []).some(
      (b) => String(b?.building_id ?? "") === selectedBuildingIdValue,
    );
  }, [buildings, selectedBuildingIdValue]);

  const selectedBuilding = buildingMap[Number(form.building_id)];

  // Persist current edit id when available (survives hard refresh)
  useEffect(() => {
    try {
      if (effectiveFloorId != null)
        localStorage.setItem("floorEditId", String(effectiveFloorId));
    } catch (_) {}
  }, [effectiveFloorId]);

  // Load the floor's details and the building it belongs to.
  useEffect(() => {
    let mounted = true;
    (async () => {
      if (effectiveFloorId == null) {
        if (mounted) setLoading(false);
        return;
      }
      try {
        setLoading(true);
        const data = await getFloorWithRelations(effectiveFloorId);
        if (!mounted || !data) return;

        setForm({
          building_id:
            data.building_id !== undefined && data.building_id !== null
              ? String(data.building_id)
              : "",
          floor_code: data.floor_code || "",
          name: data.name || "",
        });

        const related = data?.building || null;
        const id = Number(related?.building_id ?? related?.id ?? null);
        if (related && Number.isFinite(id) && id > 0) {
          setBuildings((prev) => {
            const list = Array.isArray(prev) ? prev.slice() : [];
            const normalized = {
              ...related,
              building_id: related?.building_id ?? related?.id,
            };
            const idx = list.findIndex((b) => Number(b?.building_id) === id);
            if (idx >= 0) list[idx] = { ...list[idx], ...normalized };
            else list.push(normalized);
            return list;
          });
        }
      } catch (_) {
        /* the fields stay empty; each layout reports its own failure */
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [effectiveFloorId, setForm]);

  return (
    <div className="page role-management">
      <div
        className="page-header"
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: 16,
          paddingTop: 20,
        }}
      >
        <div className="left">
          <h1>{readOnly ? "Floor" : "Edit Floor"}</h1>
          <div className="subtitle">
            {readOnly
              ? "The floor's details and its design layout, as saved. Open Edit Floor to change them."
              : "Use Floor Code and Name. Rows/Columns are inherited from the selected building."}
          </div>
        </div>
        <div className="right" style={{ flexShrink: 0, paddingRight: 12 }}>
          <button className="secondary-btn" onClick={backToList}>
            Back
          </button>
        </div>
      </div>

      <div className="page-content">
        <div className="role-card create-card" style={{ maxWidth: "100%" }}>
          {/* Only the details wait on this page's own fetch. The two layouts
              below mount straight away and load themselves — gating them behind
              this spinner made their reads start only after it finished, so the
              three requests for the same floor never overlapped and could not
              be collapsed into one. */}
          {loading ? (
            <div className="no-results">Loading...</div>
          ) : (
            <>
              <div className="field">
                <label>Building</label>
                <select value={selectedBuildingIdValue} disabled>
                  <option value="">Select a building</option>
                  {selectedBuildingIdValue && !hasSelectedBuildingOption && (
                    <option value={selectedBuildingIdValue}>
                      Building {selectedBuildingIdValue}
                    </option>
                  )}
                  {(buildings || []).map((b) => (
                    <option
                      key={b.building_id}
                      value={String(b.building_id ?? "")}
                    >
                      {b.name || `Building ${b.building_id}`}
                    </option>
                  ))}
                </select>
              </div>

              <div className="field">
                <label>Floor Code</label>
                <input
                  type="text"
                  value={form.floor_code}
                  readOnly={readOnly}
                  disabled={readOnly}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, floor_code: e.target.value }))
                  }
                />
              </div>

              <div className="field">
                <label>Floor Name</label>
                <input
                  type="text"
                  value={form.name}
                  readOnly={readOnly}
                  disabled={readOnly}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, name: e.target.value }))
                  }
                />
              </div>

              <div className="field">
                <label>Rows (inherited)</label>
                <input
                  type="number"
                  value={selectedBuilding?.rows ?? ""}
                  readOnly
                  disabled
                />
              </div>

              <div className="field">
                <label>Columns (inherited)</label>
                <input
                  type="number"
                  value={selectedBuilding?.columns ?? ""}
                  readOnly
                  disabled
                />
              </div>
            </>
          )}

          {/* The design layout: what each grid cell IS — room, corridor,
                lobby, entrance, void — and how cells combine into larger
                rooms. It comes before the desk layout because a desk can only
                go in a room, and the 3D twin walks the plan this defines. */}
          {effectiveFloorId != null && (
            <>
              <div className="fdg-divider" />
              <FloorLayoutDesigner
                floorId={effectiveFloorId}
                mode={readOnly ? "view" : "edit"}
                onLayoutChange={handleLayoutChange}
                desksVersion={desksVersion}
              />

              <div className="fdg-divider" />
              <FloorDeskLayout
                floorId={effectiveFloorId}
                mode={readOnly ? "view" : "edit"}
                refreshToken={layoutVersion}
                onDesksChange={handleDesksChange}
              />
            </>
          )}

          <div className="actions" style={{ marginTop: 16 }}>
            {readOnly ? (
              <button className="secondary-btn" onClick={backToList}>
                Back to floors
              </button>
            ) : (
              <>
                <button
                  className="primary-btn"
                  onClick={() => save()}
                  disabled={saving}
                >
                  {saving ? "Saving..." : "Save"}
                </button>
                <button
                  className="secondary-btn"
                  onClick={backToList}
                  disabled={saving}
                >
                  Cancel
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FloorEditPage;
